"use client";

import { useLocale, useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    TRAINING_FACT_PROGRAMS,
    TRAINING_FACT_ROWS,
} from "@/lib/trainingFacts";

// Les informations qu'un futur apprenant doit trouver avant de s'inscrire :
// rubriques, nombre d'objectifs et valeurs chiffrées vivent dans
// lib/trainingFacts, partagé avec le programme PDF de la formation.
const PROGRAMS = TRAINING_FACT_PROGRAMS;
const ROWS = TRAINING_FACT_ROWS;

export default function TrainingFactSheet({ program, values = {}, light = false }) {
    const t = useTranslations();
    const locale = useLocale();
    const config = PROGRAMS[program];
    if (!config) return null;

    // Les nombres passent formatés dans la langue de la page (4 500 et non 4500).
    const formatted = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
            key,
            typeof value === "number" && Number.isFinite(value)
                ? value.toLocaleString(locale)
                : value,
        ]),
    );

    const prefix = `trainingFacts_${program}`;

    // La page Python est dessinée en clair uniquement : sans ce drapeau, les
    // variantes sombres rendraient un bloc noir au milieu d'une page blanche.
    const tone = light
        ? {
              section: "bg-white border-slate-200",
              title: "text-slate-900",
              subtitle: "text-slate-600",
              row: "border-slate-200",
              label: "text-slate-500",
              value: "text-slate-800",
              icon: "text-slate-400",
              note: "text-slate-500 border-slate-200",
          }
        : {
              section:
                  "bg-white dark:bg-newDark border-gray-200 dark:border-white/[0.06]",
              title: "text-gray-900 dark:text-white",
              subtitle: "text-gray-500 dark:text-grayDark-400",
              row: "border-gray-200 dark:border-white/[0.06]",
              label: "text-gray-500 dark:text-grayDark-400",
              value: "text-gray-800 dark:text-grayDark-200",
              icon: "text-gray-400 dark:text-grayDark-500",
              note: "text-gray-500 dark:text-grayDark-500 border-gray-200 dark:border-white/[0.06]",
          };

    const renderValue = (key) => {
        if (key === "objectives") {
            return (
                <ul className="mb-0 space-y-1.5 pl-0 list-none">
                    {Array.from({ length: config.objectives }).map((_, i) => (
                        <li key={i} className="flex gap-2">
                            <span aria-hidden className={tone.icon}>
                                ·
                            </span>
                            <span>{t(`${prefix}_objective_${i + 1}`)}</span>
                        </li>
                    ))}
                </ul>
            );
        }
        return <p className="mb-0">{t(`${prefix}_${key}`, formatted)}</p>;
    };

    return (
        <section
            id="fiche-formation"
            className={`border-t px-5 py-20 md:py-28 ${tone.section}`}
        >
            <div className="mx-auto max-w-4xl">
                <div className="max-w-2xl">
                    <h2
                        className={`text-3xl md:text-4xl font-title font-bold tracking-tight ${tone.title}`}
                    >
                        {t("trainingFacts_title")}
                    </h2>
                    <p className={`mb-0 mt-4 text-base leading-relaxed ${tone.subtitle}`}>
                        {t("trainingFacts_subtitle")}
                    </p>
                </div>

                <dl className="mt-12 mb-0">
                    {ROWS.filter(
                        (row) =>
                            row.key === "objectives" ||
                            t.has(`${prefix}_${row.key}`),
                    ).map((row) => (
                        <div
                            key={row.key}
                            className={`grid gap-2 border-t py-5 md:grid-cols-[220px_1fr] md:gap-8 ${tone.row}`}
                        >
                            <dt
                                className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] ${tone.label}`}
                            >
                                <SolarIcon
                                    name={row.icon}
                                    className={`text-base ${tone.icon}`}
                                />
                                {t(`trainingFacts_label_${row.key}`)}
                            </dt>
                            <dd
                                className={`m-0 text-[15px] leading-relaxed ${tone.value}`}
                            >
                                {renderValue(row.key)}
                            </dd>
                        </div>
                    ))}
                </dl>

                <p
                    className={`mb-0 border-t pt-5 text-xs leading-relaxed ${tone.note}`}
                >
                    {t("trainingFacts_qualiopi")}
                </p>
            </div>
        </section>
    );
}
